import { Survey, SurveyControls, SurveyQuestion, useSurveyContext } from '@confer/lib'
import { getColours, hexToRgb } from '@confer/lib/utils'
import { Spin } from 'antd'
import { useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import GridLayout from './GridLayout'

interface SurveyScrollState {
  isSurveyScrolled: boolean
  scrollUiHasChanged: boolean
}

function getBackgroundColourRGB(survey: Survey) {
  const { backgroundColour } = getColours(survey.settings)
  return hexToRgb(backgroundColour)
}

const SurveyLayout = () => {
  const surveyContentRef = useRef<HTMLDivElement>(null)
  const { survey, currentQuestion, loading, error, interactionDisabled, currentPercentage } =
    useSurveyContext()
  const [surveyScrollState, setSurveyScrollState] = useState<SurveyScrollState>({
    isSurveyScrolled: false,
    scrollUiHasChanged: false,
  })
  const backgroundRGB = getBackgroundColourRGB(survey)
  const urlParams = new URLSearchParams(window.location.search)
  const lang = urlParams.get('lang') ?? 'en'

  const handleTouchMove = (e: React.UIEvent<HTMLDivElement>) => {
    const currentScrollTop = e.currentTarget.scrollTop
    const isSurveyScrolled = currentScrollTop > 0

    setSurveyScrollState((state) => ({
      isSurveyScrolled: state.scrollUiHasChanged ? state.isSurveyScrolled : isSurveyScrolled,
      scrollUiHasChanged: state.scrollUiHasChanged || isSurveyScrolled,
    }))
  }

  return (
    <GridLayout
      settings={survey.settings}
      question={currentQuestion ?? survey.questions[0]}
      currentPercentage={currentPercentage}
      isSurveyScrolled={surveyScrollState.isSurveyScrolled}
    >
      {survey.settings.showQid ? <QuestionId>{currentQuestion?.id}</QuestionId> : null}
      {loading ? (
        <>
          <Spin />
          <ErrorText>
            {error &&
              `Oops! There was an error connecting to the survey. If you know who is running the survey, please let them know something is wrong (${error.message}).`}
          </ErrorText>
        </>
      ) : (
        <SurveyContentContainer
          ref={surveyContentRef}
          onTouchMove={handleTouchMove}
          onTouchStart={() => {
            setSurveyScrollState((state) => ({ ...state, scrollUiHasChanged: false }))
          }}
          backgroundColourRGB={backgroundRGB}
          fontFamily={survey.settings.font?.family}
          noScrollShadows={currentQuestion?.type === 'welcomePage'}
          lang={lang}
          key={currentQuestion?.id}
        >
          <SurveyQuestion />
        </SurveyContentContainer>
      )}

      <SurveyControls />
      {interactionDisabled && <DisableInteractionOverlay />}
    </GridLayout>
  )
}

export const appear = keyframes`
   0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

interface SurveyContentProps {
  // settings: SurveySettings
  fontFamily?: string
  backgroundColourRGB: { r: number; g: number; b: number }
  noScrollShadows?: boolean
}

const SurveyContentContainer = styled.div<SurveyContentProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-gutter: stable both-edges;
  /* @media (max-width: 479px) {
    scrollbar-gutter: auto;
  } */
  animation: 0.5s ${appear} ease;
  font-family: ${(props) =>
    props.fontFamily ? `'${props.fontFamily}', 'Rethink Sans', sans-serif` : 'inherit'};

  /* Top/bottom shadows when content overflows - https://lea.verou.me/2012/04/background-attachment-local/ */
  @media (min-width: 480px) {
    background: ${({ backgroundColourRGB, noScrollShadows }) => {
      const { r, g, b } = backgroundColourRGB
      const bg = `rgba(${r}, ${g}, ${b}, 1)`
      const bgTrans = `rgba(${r}, ${g}, ${b}, 0)`
      const shadow = 'rgba(41, 50, 56, 0.5)'
      return `
    linear-gradient(${bg} 30%, ${bgTrans}) center top,
    linear-gradient(${bgTrans}, ${bg} 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, ${shadow}, rgba(0,0,0,0)) center top,
    radial-gradient(farthest-side at 50% 100%, ${shadow}, rgba(0,0,0,0)) center bottom
    `
    }};
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 14px,
      100% 14px;
    background-attachment: local, local, scroll, scroll;
  }

  @media (max-width: 479px) {
    background: ${({ backgroundColourRGB, noScrollShadows }) => {
      if (noScrollShadows) {
        return ''
      }
      const { r, g, b } = backgroundColourRGB
      const bg = `rgba(${r}, ${g}, ${b}, 1)`
      const bgTrans = `rgba(${r}, ${g}, ${b}, 0)`
      const shadow = 'rgba(41, 50, 56, 0.5)'
      return `
    linear-gradient(${bg} 30%, ${bgTrans}) center top,
    linear-gradient(${bgTrans}, ${bg} 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, ${shadow}, rgba(0,0,0,0)) center top,
    radial-gradient(farthest-side at 50% 100%, ${shadow}, rgba(0,0,0,0)) center bottom
    `
    }};
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 14px,
      100% 14px;
    background-attachment: local, local, scroll, scroll;
  }
`

const QuestionId = styled.p`
  color: red;
  position: absolute;
  top: 30px;
  right: 10%;
`

const ErrorText = styled.div`
  margin: 1rem;
`

const DisableInteractionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
`

export default SurveyLayout
